/*
  Zen Cart Add -- one offer selection = one Add to Cart (Oct 2026)
  ================================================================
  Naeem (3 Oct): picking one offer showed up as 5-6 AddToCart events in Meta Test Events.

  Why: Shopify fires product_added_to_cart once PER LINE added through the Ajax cart
  (/cart/add.js, /cart/update.js, /cart/change.js, permalinks -- see zb-offer-confirm.js),
  and the Facebook & Instagram app turns each one into a Meta AddToCart. An offer adds the
  coffee plus every free gift and the "Bundle Savings" / "Free Shipping" lines, so 1 click
  became 5-6 ATCs.

  Fix: add(items) puts items[0] (the paid offer line) in through /cart/add.js exactly as
  before -- that is the ONE Add to Cart -- and every remaining line through the Storefront
  API (cartLinesAdd, tokenless) on the SAME cart. Shopify emits no product_added_to_cart for
  Storefront API adds (proved on the live store 3 Oct: coffee + 2 extra lines = 1 Meta
  AddToCart, vs 1 + 2 = 3 through /cart/add.js). The lines, their properties, the gift
  discounts and the checkout are identical either way.

  Safety: if the Storefront API call fails for any reason, the same lines go through
  /cart/add.js instead. The cart is always right; only the event count falls back.
  zen-gift-guard.js only watches /cart/* requests, so add() runs its reconcile itself once
  the Storefront lines are in.
*/
(function () {
  "use strict";

  if (window.ZenCartAdd) return;

  var API = "/api/2026-07/graphql.json";
  var MUTATION =
    "mutation ZenCartAdd($cartId: ID!, $lines: [CartLineInput!]!) {" +
    " cartLinesAdd(cartId: $cartId, lines: $lines) {" +
    " cart { lines(first: 100) { nodes { quantity attributes { key value } merchandise { ... on ProductVariant { id } } } } }" +
    " userErrors { field message } } }";

  function ajax(url, body) {
    var init = { headers: { Accept: "application/json" } };
    if (body) {
      init.method = "POST";
      init.headers["Content-Type"] = "application/json";
      init.body = JSON.stringify(body);
    }
    return window.fetch(url, init).then(function (response) {
      return response.json().catch(function () { return {}; }).then(function (data) {
        if (!response.ok) throw new Error((data && (data.description || data.message)) || "Cart request failed");
        return data;
      });
    });
  }

  function toLine(item) {
    var line = {
      merchandiseId: "gid://shopify/ProductVariant/" + item.id,
      quantity: Number(item.quantity) || 1
    };
    var props = item.properties || {};
    var attributes = Object.keys(props).filter(function (key) {
      return props[key] !== undefined && props[key] !== null && props[key] !== "";
    }).map(function (key) {
      return { key: key, value: String(props[key]) };
    });
    if (attributes.length) line.attributes = attributes;
    if (item.selling_plan) line.sellingPlanId = "gid://shopify/SellingPlan/" + item.selling_plan;
    return line;
  }

  /* Resolves with the items that did NOT land (normally []), so only those are retried. */
  function addQuietly(items) {
    return ajax("/cart.js").then(function (cart) {
      if (!cart || !cart.token) throw new Error("No cart token");
      return window.fetch(API, {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({
          query: MUTATION,
          variables: { cartId: "gid://shopify/Cart/" + cart.token, lines: items.map(toLine) }
        })
      });
    }).then(function (response) {
      return response.json().then(function (data) {
        var result = data && data.data && data.data.cartLinesAdd;
        var problem = !response.ok || (data && data.errors && data.errors.length) || !result || !result.cart ||
          (result.userErrors && result.userErrors.length);
        if (problem) throw new Error("Storefront cartLinesAdd failed: " + JSON.stringify((data && data.errors) || (result && result.userErrors) || response.status));
        // A sold-out variant comes back as a warning with no line, not as an error. Match on
        // variant AND properties: Kids Cafe adds the same flavor as separate pouch lines.
        var nodes = result.cart.lines.nodes;
        return items.filter(function (item) {
          var line = toLine(item);
          return !nodes.some(function (node) {
            if (!node.merchandise || node.merchandise.id !== line.merchandiseId) return false;
            var attrs = {};
            (node.attributes || []).forEach(function (a) { attrs[a.key] = a.value; });
            return (line.attributes || []).every(function (a) { return attrs[a.key] === a.value; });
          });
        });
      });
    });
  }

  /* Takes the first line back out when the rest cannot be added, so a failed offer
     leaves the cart as it was -- the same all-or-nothing result as one /cart/add.js. */
  function rollback(first, firstItem) {
    var line = first && first.items && first.items[0];
    if (!line || !line.key) return Promise.resolve();
    var keep = Math.max(0, (Number(line.quantity) || 0) - (Number(firstItem.quantity) || 1));
    return ajax("/cart/change.js", { id: line.key, quantity: keep }).catch(function () {});
  }

  /* items: the same array a caller would POST to /cart/add.js, paid offer line FIRST.
     Resolves with the /cart/add.js response for that first line. */
  function add(items) {
    items = (items || []).filter(Boolean);
    if (!items.length) return Promise.reject(new Error("Nothing to add"));
    var rest = items.slice(1);
    return ajax("/cart/add.js", { items: [items[0]] }).then(function (first) {
      if (!rest.length) return first;
      return addQuietly(rest)
        .catch(function (error) {
          if (window.console) console.warn("[ZenCartAdd] Storefront add failed, using /cart/add.js:", error);
          return rest;
        })
        .then(function (missing) {
          if (missing.length) return ajax("/cart/add.js", { items: missing });
          // The guard only watches /cart/* requests, so run it for the lines it did not see.
          var guard = window.ZenGiftGuard;
          return guard && guard.reconcile ? guard.reconcile().catch(function () {}) : null;
        })
        .then(function () { return first; }, function (error) {
          return rollback(first, items[0]).then(function () { throw error; });
        });
    });
  }

  window.ZenCartAdd = { add: add };
})();
